import React, { useCallback, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { radTemplateApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Table, Modal, Field, Section, Loading } from "../../Components/UI/ui";
import { GATE, MODALITIES, REPORT_SECTIONS, modalityLabel } from "../../lib/lab";

// Settings for radiology: the lab's scan templates — the default report text a
// radiologist starts from. Normally cloned from the shared catalogue.
const ScanTemplates = () => {
  const { can } = useAuth();
  const { toast, skus, loadSkus } = useApp();
  const write = can(...GATE.radTemplatesWrite);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modality, setModality] = useState("");
  const [q, setQ] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (modality) params.modality = modality;
      if (q.trim()) params.q = q.trim();
      const res = await radTemplateApi.list(params);
      setRows(res?.data || res || []);
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [modality, q, toast]);

  useEffect(() => {
    const h = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, q]);
  useEffect(() => { loadSkus(); }, [loadSkus]);

  const toggle = async (t) => {
    setBusy(t._id);
    try { await radTemplateApi.toggle(t._id); toast.success(t.isActive ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const columns = [
    { key: "name", title: "Scan", render: (t) => (<><div className="primary">{t.name}</div><div className="sub">{t.bodyPart || ""}{t.views?.length ? ` · ${t.views.join(", ")}` : ""}</div></>) },
    { key: "modality", title: "Modality", render: (t) => <Badge kind="radiology">{modalityLabel(t.modality)}</Badge> },
    { key: "skus", title: "Sold as", render: (t) => (t.skus?.length
        ? <span className="sub">{t.skus.map((k) => k.name).join(", ")}</span>
        : <Badge kind="unpaid">Not linked to a price</Badge>) },
    { key: "version", title: "Version", render: (t) => `v${t.version}` },
    { key: "isActive", title: "Status", render: (t) => <Badge kind={t.isActive ? "on" : "off"}>{t.isActive ? "Active" : "Inactive"}</Badge> },
    ...(write ? [{ key: "a", title: "", render: (t) => (
      <div className="lab-row end" style={{ gap: 4 }}>
        <Button size="xs" onClick={() => setEditing(t._id)}>Edit</Button>
        <Button size="xs" loading={busy === t._id} onClick={() => toggle(t)}>{t.isActive ? "Deactivate" : "Activate"}</Button>
      </div>
    ) }] : []),
  ];

  const unlinked = rows.filter((t) => t.isActive && !(t.skus || []).length).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", minHeight: 0 }}>
      <div className="lab-toolbar">
        <input className="lab-input grow" placeholder="Search scans" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="lab-select" value={modality} onChange={(e) => setModality(e.target.value)}>
          <option value="">All modalities</option>
          {MODALITIES.map((m) => <option key={m} value={m}>{modalityLabel(m)}</option>)}
        </select>
        {write && <Button variant="primary" onClick={() => setCatOpen(true)}><IoMdAdd /> Add from catalogue</Button>}
      </div>
      {unlinked > 0 && <div className="lab-danger-text">{unlinked} scan template{unlinked === 1 ? " is" : "s are"} not linked to a priced item — bookings of those scans produce no study.</div>}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table columns={columns} rows={rows} loading={loading} onRowClick={write ? (t) => setEditing(t._id) : undefined} empty="No scan templates yet — add one from the catalogue" />
      </div>

      <CatalogueModal open={catOpen} onClose={() => setCatOpen(false)} onAdded={(t) => { toast.success(`${t.name} added`); load(); setEditing(t._id); }} />
      <TemplateEditorModal id={editing} open={!!editing} onClose={() => setEditing(null)} skus={skus} onSaved={load} />
    </div>
  );
};

// Clone a procedure from the shared dictionary (~49 seeded)
const CatalogueModal = ({ open, onClose, onAdded }) => {
  const [modality, setModality] = useState("");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [mods, setMods] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) { setQ(""); setModality(""); setPicked(null); setName(""); setErr(null); radTemplateApi.modalities().then((m) => setMods(m?.data || m || [])).catch(() => {}); } }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await radTemplateApi.catalogue({ modality: modality || undefined, q: q.trim() || undefined, limit: 40 });
        if (!cancelled) setHits(res?.data || res || []);
      } catch (e) { if (!cancelled) setErr(apiError(e)); } finally { if (!cancelled) setBusy(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(h); };
  }, [open, modality, q]);

  const add = async () => {
    if (!picked) return;
    setBusy(true); setErr(null);
    try {
      const body = { procedureId: picked.procedureId };
      if (name.trim() && name.trim() !== picked.canonicalName) body.name = name.trim();
      onAdded(await radTemplateApi.fromCatalogue(body));
      onClose();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add a scan from the catalogue"
      footer={<>
        {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!picked} loading={busy} onClick={add}>Add &amp; edit</Button>
      </>}>
      <div className="lab-row">
        <select className="lab-select" value={modality} onChange={(e) => { setModality(e.target.value); setPicked(null); }}>
          <option value="">All modalities</option>
          {(mods.length ? mods : MODALITIES.map((m) => ({ modality: m }))).map((m) => (
            <option key={m.modality} value={m.modality}>{modalityLabel(m.modality)}{m.procedures != null ? ` (${m.procedures})` : ""}</option>
          ))}
        </select>
        <input className="lab-input" style={{ flex: 1 }} placeholder="Search e.g. chest, knee, abdomen" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      </div>
      <div className="lab-list" style={{ maxHeight: 300 }}>
        {busy && !hits.length && <span className="lab-muted">Searching…</span>}
        {hits.map((h) => (
          <div key={h.procedureId} className={`lab-list__item ${picked?.procedureId === h.procedureId ? "selected" : ""}`} onClick={() => { setPicked(h); setName(h.canonicalName); }}>
            <div>
              <div style={{ fontWeight: 600 }}>{h.canonicalName}</div>
              <div className="lab-muted">{modalityLabel(h.modality)}{h.bodyPart ? ` · ${h.bodyPart}` : ""}{h.views?.length ? ` · ${h.views.join(", ")}` : ""}{h.contrastTypical ? " · contrast" : ""}</div>
            </div>
            {h.aliases?.length > 0 && <span className="lab-muted">{h.aliases.slice(0, 2).join(", ")}</span>}
          </div>
        ))}
        {!busy && !hits.length && <span className="lab-muted">Nothing matches.</span>}
      </div>
      {picked && <Field label="Name in this lab"><input className="lab-input w-full" value={name} onChange={(e) => setName(e.target.value)} /></Field>}
    </Modal>
  );
};

// Edit the boilerplate a radiologist starts from, and link it to priced items
const TemplateEditorModal = ({ id, open, onClose, skus, onSaved }) => {
  const { toast } = useApp();
  const [t, setT] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [linkSku, setLinkSku] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const tpl = await radTemplateApi.get(id);
      setT(tpl);
      setF({ name: tpl.name || "", bodyPart: tpl.bodyPart || "", views: (tpl.views || []).join(", "), reportNotes: tpl.reportNotes || "", sections: { ...(tpl.sections || {}) } });
    } catch (e) { toast.error(e); onClose(); }
  }, [id, toast, onClose]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setBusy("save"); setErr(null);
    try {
      const saved = await radTemplateApi.update(id, {
        name: f.name.trim(), bodyPart: f.bodyPart.trim(),
        views: f.views.split(",").map((v) => v.trim()).filter(Boolean),
        reportNotes: f.reportNotes.trim(), sections: f.sections,
      });
      setT(saved);
      toast.success(saved.version !== t.version ? `Saved · now version ${saved.version}` : "Saved");
      onSaved?.();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(null); }
  };

  const doLink = async () => {
    if (!linkSku) return;
    setBusy("link");
    try { await radTemplateApi.linkSku(id, linkSku); setLinkSku(""); toast.success("Linked"); load(); onSaved?.(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  const doUnlink = async (skuId) => {
    setBusy(skuId);
    try { await radTemplateApi.unlinkSku(id, skuId); toast.success("Unlinked"); load(); onSaved?.(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const linked = t?.skus || t?.linkedSkus || [];
  const linkedIds = new Set(linked.map((k) => k._id));

  return (
    <Modal open={open} onClose={onClose} size="lg" title={t ? `${t.name} · v${t.version}` : "Scan template"}
      footer={<>
        {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
        <Button onClick={onClose}>Close</Button>
        <Button variant="primary" loading={busy === "save"} onClick={save}>Save</Button>
      </>}>
      {!f ? <Loading /> : (
        <>
          <div className="lab-grid cols-3">
            <Field label="Name"><input className="lab-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Body part"><input className="lab-input" value={f.bodyPart} onChange={(e) => setF({ ...f, bodyPart: e.target.value })} /></Field>
            <Field label="Views" hint="Comma separated, e.g. PA, Lateral"><input className="lab-input" value={f.views} onChange={(e) => setF({ ...f, views: e.target.value })} /></Field>
          </div>
          <div className="lab-muted">
            This text is copied into every new study of this scan, so the radiologist edits rather than starts from a blank page. Changing it bumps the version; reports already issued keep their own copy.
          </div>
          {REPORT_SECTIONS.map(({ key, label, rows }) => (
            <Field key={key} label={`Default ${label.toLowerCase()}`}>
              <textarea className="lab-textarea w-full" rows={Math.min(rows, 6)} value={f.sections?.[key] ?? ""} onChange={(e) => setF({ ...f, sections: { ...f.sections, [key]: e.target.value } })} />
            </Field>
          ))}
          <Field label="Report notes" hint="Printed under the report"><input className="lab-input w-full" value={f.reportNotes} onChange={(e) => setF({ ...f, reportNotes: e.target.value })} /></Field>

          <Section title="Sold as">
            <div className="lab-row" style={{ marginBottom: 8 }}>
              {linked.length === 0 && <span className="lab-muted">Not linked to a priced item — bookings of that scan produce no study.</span>}
              {linked.map((k) => (
                <span key={k._id} className="lab-tag">
                  {k.name}
                  <button type="button" className="lab-link" style={{ marginLeft: 6 }} onClick={() => doUnlink(k._id)} disabled={busy === k._id}>✕</button>
                </span>
              ))}
            </div>
            <div className="lab-row">
              <select className="lab-select" style={{ flex: 1 }} value={linkSku} onChange={(e) => setLinkSku(e.target.value)}>
                <option value="">Link a catalogue item…</option>
                {skus.filter((k) => k.serviceType === "radiology" && !linkedIds.has(k._id)).map((k) => <option key={k._id} value={k._id}>{k.name}</option>)}
              </select>
              <Button size="sm" disabled={!linkSku} loading={busy === "link"} onClick={doLink}>Link</Button>
            </div>
          </Section>
        </>
      )}
    </Modal>
  );
};


export default ScanTemplates;
